import BigNumber from 'bignumber.js';

/**
 * Money class to represent amount and currency. Always stores money in lowest
 * denomination in 18 decimals.
 */
export default class Money {

    /**
     * Create a new instance of Money
     * @param amount
     * @param currency_id
     * @param currency_name
     * @param currency_symbol
     */
    constructor({amount, currency_id, currency_name, currency_symbol}) {
        this.amount          = amount instanceof Money ? amount : new BigNumber(amount || 0);
        this.currency_id     = currency_id;
        this.currency_name   = currency_name;
        this.currency_symbol = currency_symbol;

        this.format = {
            prefix: this.currency_symbol,
            decimalSeparator: '.',
            groupSeparator: ',',
            groupSize: 3,
            secondaryGroupSize: 0,
            fractionGroupSeparator: ' ',
            fractionGroupSize: 0,
            suffix: ''
        };
    }

    /**
     * New instance of USD Money
     * @param amount
     * @returns {Money}
     */
    static fromUSDCents(amount) {
        return new Money({
            amount: amount,
            currency_id: 158,
            currency_name: "US Dollar",
            currency_symbol: "USD$"
        });
    }

    /**
     * New instance of USD Money
     * @param amount
     * @returns {Money}
     */
    static fromUSDDollars(amount) {
        return new Money({
            amount: new BigNumber(amount).times("1000000000000000000"),
            currency_id: 158,
            currency_name: "US Dollar",
            currency_symbol: "USD$"
        });
    }

    /**
     * Is this money > that money
     * @param that money instance
     */
    gt(that) {
        if(!this.isSameCurrency(that)) throw new Error("that is not same currency as this.");

        return this.amount.gt(that.amount);
    }

    /**
     * Is this money >= that money
     * @param that money instance
     */
    gte(that) {
        if(!this.isSameCurrency(that)) throw new Error("that is not same currency as this.");

        return this.amount.gte(that.amount);
    }

    /**
     * Is this money < that money
     * @param that money instance
     */
    lt(that) {
        if(!this.isSameCurrency(that)) throw new Error("that is not same currency as this.");

        return this.amount.lt(that.amount);
    }

    /**
     * Is this money <= that money
     * @param that money instance
     */
    lte(that) {
        if(!this.isSameCurrency(that)) throw new Error("that is not same currency as this.");

        return this.amount.lte(that.amount);
    }

    /**
     * Is this money == that money
     * @param that money instance
     */
    eq(that) {
        if(!this.isSameCurrency(that)) throw new Error("that is not same currency as this.");

        return this.amount.eq(that.amount);
    }

    /**
     * Compare currencies. Returns true if same. Otherwise, returns false.
     * @param that money instance
     */
    isSameCurrency(that) {
        if(!(that instanceof Money)) throw new Error("that is not an instance of Money");
        return this.currency_id === that.currency_id && this.currency_name === that.currency_name && this.currency_symbol === that.currency_symbol;
    }

    toFixed(){
        return this.amount.toFixed();
    }

    /**
     * To be used in presentation
     */
    toString() {
        return this.amount.toFormat(2, this.format);
    }

    /**
     * To be used in presentation
     */
    toFullString() {
        return this.amount.toFormat(18, this.format);
    }

    /**
     * To be used in presentation
     */
    toFormat(fmt) {
        let merged = Object.assign({}, this.format, fmt);
        return this.amount.toFormat(2, merged);
    }
}